import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, Snackbar, Alert, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
  MenuItem, Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import config from '../../../config';

const MEAL_TYPES = ['Breakfast', 'Lunch', 'Dinner', 'Snack'];

// HR Staff Meals — daily meal log per employee with cost. Roll-ups (per-
// month cost per employee) will integrate into payroll as a deduction in a
// future enhancement.
export default function StaffMeals() {
  const token = sessionStorage.getItem('token');
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const companyId = company?.id;
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = (() => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); })();

  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [empFilter, setEmpFilter] = useState('');
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);
  const [typeFilter, setTypeFilter] = useState('');

  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ employeeId: '', mealDate: today, mealType: 'Lunch', cost: 0, remarks: '' });

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const toast = (m, s = 'success') => setSnack({ open: true, msg: m, sev: s });

  const loadEmployees = async () => {
    const res = await fetch(`${config.API_BASE_URL}/Hr/Employees/${companyId}`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setEmployees(await res.json());
  };

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (empFilter) qs.append('employeeId', empFilter);
      if (fromDate) qs.append('from', fromDate);
      if (toDate) qs.append('to', toDate);
      const res = await fetch(`${config.API_BASE_URL}/Hr/StaffMeals/${companyId}?${qs.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      setRows(await res.json());
    } catch (e) { toast('Failed to load', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (companyId) loadEmployees(); /* eslint-disable-next-line */ }, [companyId]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId, empFilter, fromDate, toDate]);

  const filtered = useMemo(
    () => (typeFilter ? rows.filter((r) => r.mealType === typeFilter) : rows),
    [rows, typeFilter],
  );

  const totals = useMemo(() => ({
    count: filtered.length,
    cost: filtered.reduce((a, r) => a + Number(r.cost || 0), 0),
    byType: MEAL_TYPES.reduce((acc, t) => {
      acc[t] = filtered.filter((r) => r.mealType === t).reduce((a, r) => a + Number(r.cost || 0), 0);
      return acc;
    }, {}),
  }), [filtered]);

  const save = async () => {
    if (!form.employeeId) { toast('Pick employee', 'warning'); return; }
    const body = {
      Id: null, CompanyId: companyId,
      EmployeeId: Number(form.employeeId),
      MealDate: form.mealDate,
      MealType: form.mealType,
      Cost: Number(form.cost) || 0,
      Remarks: form.remarks || null,
    };
    const res = await fetch(`${config.API_BASE_URL}/Hr/StaffMeal`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || !data.success) { toast('Save failed', 'error'); return; }
    toast('Meal recorded');
    setDialog(false);
    load();
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.mealType} for ${r.employeeName} on ${new Date(r.mealDate).toLocaleDateString('en-IN')}?`)) return;
    const res = await fetch(`${config.API_BASE_URL}/Hr/StaffMeal/${r.id}/${companyId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { toast('Deleted'); load(); } else toast('Delete failed', 'error');
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#F1F5F9', minHeight: '100%' }}>
      <Stack direction='row' spacing={1.5} alignItems='center' sx={{ mb: 2, justifyContent: 'space-between' }}>
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box sx={{ width: 42, height: 42, borderRadius: 2, bgcolor: '#f59e0b', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LocalDiningIcon />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>Staff Meals</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>HR · {totals.count} meal(s) · ₹{totals.cost.toLocaleString('en-IN')}</Typography>
          </Box>
        </Stack>
        <Button variant='contained' startIcon={<AddIcon />}
          onClick={() => { setForm({ employeeId: '', mealDate: today, mealType: 'Lunch', cost: 0, remarks: '' }); setDialog(true); }}
          sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>
          New Meal
        </Button>
      </Stack>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {MEAL_TYPES.map((t, i) => {
          const colors = ['#fbbf24', '#16a34a', '#ec4899', '#0ea5e9'];
          return (
            <Grid item xs={6} sm={3} key={t}>
              <Card sx={{ borderLeft: `4px solid ${colors[i]}`, p: 1.5 }}>
                <Typography sx={{ fontSize: '0.7rem', color: '#64748B' }}>{t}</Typography>
                <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>₹{Number(totals.byType[t] || 0).toLocaleString('en-IN')}</Typography>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField select size='small' label='Employee' value={empFilter} onChange={(e) => setEmpFilter(e.target.value)} sx={{ bgcolor: '#fff', minWidth: 220 }}>
          <MenuItem value=''>All</MenuItem>
          {employees.map((e) => <MenuItem key={e.id} value={e.id}>{e.employeeName} ({e.employeeCode})</MenuItem>)}
        </TextField>
        <TextField select size='small' label='Meal Type' value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} sx={{ bgcolor: '#fff', minWidth: 140 }}>
          <MenuItem value=''>All</MenuItem>
          {MEAL_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
        <TextField type='date' size='small' label='From' InputLabelProps={{ shrink: true }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} sx={{ bgcolor: '#fff' }} />
        <TextField type='date' size='small' label='To' InputLabelProps={{ shrink: true }} value={toDate} onChange={(e) => setToDate(e.target.value)} sx={{ bgcolor: '#fff' }} />
      </Stack>

      <Card sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
          ) : (
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Department</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>Cost</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Remarks</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 && <TableRow><TableCell colSpan={7} align='center' sx={{ py: 4 }}><Typography sx={{ color: '#94a3b8' }}>No meal entries.</Typography></TableCell></TableRow>}
                  {filtered.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{new Date(r.mealDate).toLocaleDateString('en-IN')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{r.employeeName}<Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>{r.employeeCode}</Typography></TableCell>
                      <TableCell>{r.departmentName || '—'}</TableCell>
                      <TableCell><Chip size='small' label={r.mealType} sx={{ bgcolor: '#fef3c7', fontWeight: 600 }} /></TableCell>
                      <TableCell align='right' sx={{ fontWeight: 600 }}>₹{Number(r.cost).toLocaleString('en-IN')}</TableCell>
                      <TableCell sx={{ color: '#475569' }}>{r.remarks || '—'}</TableCell>
                      <TableCell align='right'>
                        <Tooltip title='Delete'><IconButton size='small' onClick={() => remove(r)} sx={{ color: '#dc2626' }}><DeleteOutlineIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>New Meal Entry</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField select size='small' fullWidth label='Employee *' value={form.employeeId} onChange={(e) => setForm({ ...form, employeeId: e.target.value })}>
                {employees.map((e) => <MenuItem key={e.id} value={e.id}>{e.employeeName} ({e.employeeCode})</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}><TextField type='date' size='small' fullWidth label='Date *' InputLabelProps={{ shrink: true }} value={form.mealDate} onChange={(e) => setForm({ ...form, mealDate: e.target.value })} /></Grid>
            <Grid item xs={6}>
              <TextField select size='small' fullWidth label='Meal Type *' value={form.mealType} onChange={(e) => setForm({ ...form, mealType: e.target.value })}>
                {MEAL_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}><TextField type='number' size='small' fullWidth label='Cost (₹)' value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField size='small' fullWidth multiline rows={2} label='Remarks' value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant='contained' onClick={save} sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.sev} variant='filled'>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
